import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { cn } from '@Utils/cn';
import { presenceLinesFor } from '@Constants/operations';
import type { AgentProfile, AgentStatus } from '@Types/agent';
import StatusDot from '@Views/souls/desk/StatusDot';
import PresenceLine from '@Views/souls/desk/PresenceLine';
import { useCountUp } from '@Views/souls/desk/useCountUp';

const STATUS_LABEL: Record<AgentStatus, string> = {
  active: 'On duty',
  working: 'Working',
  idle: 'Idle',
};

function StatChip({ value, label }: { value: number; label: string }) {
  const n = useCountUp(value);
  return (
    <div className="rounded-lg border bg-white px-3 py-1.5">
      <span className="text-lg font-bold text-zinc-900">{n}</span>
      <span className="ml-1.5 text-xs text-zinc-500">{label}</span>
    </div>
  );
}

export default function AgentPersonaHeader({
  profile,
}: {
  profile: AgentProfile;
  showEdit?: boolean;
}) {
  const lastRan = DateTime.fromISO(profile.lastActiveAt);
  const presenceLines = useMemo(
    () => presenceLinesFor(profile.operations),
    [profile.operations],
  );

  return (
    <div className="flex items-center gap-5 py-6">
      <div className="relative shrink-0">
        <img
          src={profile.avatarUrl ?? ''}
          alt={profile.name}
          className="h-20 w-20 rounded-full bg-zinc-100 object-cover shadow ring-2 ring-white"
        />
        <StatusDot
          status={profile.status}
          className="absolute right-1 bottom-1"
        />
      </div>

      <div className="min-w-0">
        <Heading level="h2">{profile.name}</Heading>
        {profile.description && (
          <p className="text-sm text-zinc-500">{profile.description}</p>
        )}
        <p className="mt-1 text-xs font-medium text-zinc-600">
          <span
            className={cn(
              profile.status === 'active' && 'text-green-600',
              profile.status === 'working' && 'text-amber-600',
              profile.status === 'idle' && 'text-zinc-500',
            )}
          >
            {STATUS_LABEL[profile.status]}
          </span>
          {lastRan.isValid && <> · last ran {lastRan.toRelative()}</>}
        </p>

        <PresenceLine lines={presenceLines} className="mt-1.5" />

        <div className="mt-3 flex flex-wrap gap-2">
          {Object.values(profile.operationStats ?? {})
            .flat()
            .map((s) => (
              <StatChip key={s.label} value={s.value} label={s.label} />
            ))}
        </div>
      </div>
    </div>
  );
}
